import { defineStore } from 'pinia';
import { markRaw } from 'vue';

const MAX_ENTRIES = 200;

export const useUndoStore = defineStore('undo', {
    state: () => ({
        entries: [],
        cursor: -1,
        // Invalidates consumers when history changes, including an in-place
        // merge of two consecutive commands.
        historyVersion: 0,
    }),

    getters: {
        canUndo: (state) => state.cursor >= 0,
        canRedo: (state) => state.cursor < state.entries.length - 1,
    },

    actions: {
        push(entry) {
            this.historyVersion++;
            // Truncate any redo future
            if (this.cursor < this.entries.length - 1) {
                this.entries.splice(this.cursor + 1);
            }

            // Coalesce with last entry if supported
            const last = this.entries.at(-1);
            if (last?.canMergeWith?.(entry)) {
                last.mergeWith(entry);
                last.historyRevision = this.historyVersion;
            } else {
                entry.historyRevision = this.historyVersion;
                this.entries.push(markRaw(entry));
                if (this.entries.length > MAX_ENTRIES) {
                    this.entries.shift();
                }
            }

            this.cursor = this.entries.length - 1;
        },

        async undo() {
            if (!this.canUndo) return;
            const entry = this.entries[this.cursor];
            await entry.undo();
            this.cursor--;
            this.historyVersion++;
        },

        async redo() {
            if (!this.canRedo) return;
            this.cursor++;
            const entry = this.entries[this.cursor];
            await entry.execute();
            this.historyVersion++;
        },

        async jumpTo(index) {
            if (index < 0 || index >= this.entries.length) return;
            const previousCursor = this.cursor;
            while (this.cursor > index) {
                await this.entries[this.cursor].undo();
                this.cursor--;
            }
            while (this.cursor < index) {
                this.cursor++;
                await this.entries[this.cursor].execute();
            }
            if (this.cursor !== previousCursor) this.historyVersion++;
        },

        clear() {
            this.entries = [];
            this.cursor = -1;
            this.historyVersion++;
        },
    },
});
